// @ts-strict-ignore
import {inject, Injectable, signal} from "@angular/core";
import {SearchableColumn, SearchFilter} from "@models";
import {FormControl, FormGroup} from "@angular/forms";
import {SimEventService, SimUserExtendedService} from "@services";
import {FetchData_TypeMapping} from "../sim-advanced-inputs/autocomplete/autocomplete.service";
import {SimChipAutocompleteComponent} from "../sim-advanced-inputs/autocomplete/sim-chip-autocomplete/sim-chip-autocomplete.component";

const AUTOCOMPLETE_TYPES = {User: true, Event: true} as const;
/**
 * Holds the form state used in the advanced-search and applied-filters components.
 * Should be provided in the component that uses the administration table (page component).
 */
@Injectable()
export class AdministrationTableFormService {
	private _isFormLoaded = signal(false);
	private _simUserService = inject(SimUserExtendedService);
	private _simEventService = inject(SimEventService);
	private _autoCompleteComponentsMap = new Map<string, SimChipAutocompleteComponent<keyof FetchData_TypeMapping>>();

	public formGroup = new FormGroup({});
	public isFormLoaded = this._isFormLoaded.asReadonly();

	public async initializeForm(searchableColumns: SearchableColumn[], searchFilters: SearchFilter[]) {
		for (const col of searchableColumns) {
			const searchTerms = searchFilters.find((filter) => filter.searchColumn === col.value)?.searchTerms || null;
			await this._addSearchableColumnToForm(col, searchTerms);
		}
		this._isFormLoaded.set(true);
	}

	private async _addSearchableColumnToForm(col: SearchableColumn, searchTerms: string[] | null) {
		const firstSearchTerm = searchTerms?.[0] || null;
		if (col.type in AUTOCOMPLETE_TYPES) {
			await this._addAutoCompleteColumnToForm(col, firstSearchTerm);
		} else if (col.type === "MultiSelect") {
			this._addMultiSelectColumnToForm(searchTerms, col);
		} else {
			this._addDefaultColumnToForm(firstSearchTerm, col);
		}
	}

	private _addDefaultColumnToForm(firstSearchTerm: string, col: SearchableColumn) {
		const control = new FormControl("");
		if (firstSearchTerm) {
			control.setValue(firstSearchTerm);
		}
		this.formGroup.addControl(col.value, control);
	}

	private _addMultiSelectColumnToForm(searchTerms: string[], col: SearchableColumn) {
		const control = new FormControl([]);
		if (searchTerms?.length > 0) {
			control.setValue(searchTerms);
		}
		this.formGroup.addControl(col.value, control);
	}

	private async _addAutoCompleteColumnToForm(col: SearchableColumn, firstSearchTerm: string) {
		switch (col.type as keyof typeof AUTOCOMPLETE_TYPES) {
			case "User": {
				const user = firstSearchTerm ? await this._simUserService.getUserById(firstSearchTerm) : null;
				this.formGroup.addControl(col.value, new FormControl(user));
				break;
			}
			case "Event": {
				const event = firstSearchTerm ? await this._simEventService.getEventById(firstSearchTerm) : null;
				this.formGroup.addControl(col.value, new FormControl(event));
				break;
			}
		}
	}

	public registerAutoCompleteComponent(component: SimChipAutocompleteComponent<keyof FetchData_TypeMapping>, id: string) {
		this._autoCompleteComponentsMap.set(id, component);
	}

	public clearAllFilters(searchableColumns: SearchableColumn[]) {
		for (const col of searchableColumns) {
			this.clearFilter(col);
		}
	}

	public clearFilter(column: SearchableColumn) {
		const control = this.formGroup.controls[column.value];
		if (column.type in AUTOCOMPLETE_TYPES) {
			const autoCompleteComponent = this._autoCompleteComponentsMap.get(column.value);
			autoCompleteComponent?.removeSelection();
			autoCompleteComponent?.trigger().closePanel();
		} else if (column.type === "MultiSelect") {
			control.setValue([]);
		} else {
			control.reset(null);
		}
	}
}
